import {
    Container,
} from 'pixi.js';
import { Button, FocusManager, Focusable, InteractiveWrapper, StyledBitmapText } from '../../ui';
import { AccessibilityPopover } from './accessibilityPopover';
import { RoundedRect } from './ui';
import { Menus } from './Menus';
import { LoadContext, DisposableGroup } from '../../utils';

export class IntroMenu extends Container
{
    private popover:AccessibilityPopover;
    private buttons: Button[];
    private titleBg: RoundedRect;
    private disposables: DisposableGroup;

    public static preload()
    {
        LoadContext.current.add('Settings', './assets/accessibility/Settings{size}.json');
        RoundedRect.preload();
    }

    constructor( popover:AccessibilityPopover )
    {
        super();
        this.disposables = new DisposableGroup();
        this.popover = popover;
        const loader = LoadContext.current;

        // Title
        this.titleBg = new RoundedRect( loader.getSpritesheet('Settings').textures['whitesquare'], 1026, 233, true );
        this.titleBg.position.set( 552, 122 );
        this.addChild( this.titleBg );

        this.titleBg.interactive = true;
        this.disposables.add(new InteractiveWrapper(this.titleBg, {
            onHover: () => {
                this.playInfoAudio();
            },
            onTap: () => {
                this.playInfoAudio();
            }
        }));

        const titleText = new StyledBitmapText( "Choose the parts of the game you want to adjust", {
            align: 'center',
            fontName: 'AsapMedium',
            fontSize: 80,
            tint: 0x000000,
            // wordWrap: true,
            maxWidth: 900
        } );
        titleText.anchor.set( 0.5, 0.6 );
        titleText.position.set( 513, 116 );
        this.titleBg.addChild( titleText );

        this.buttons = [];
        const buttonTextStyle = {
            fontName: 'Asap',
            fontSize: 43,
            tint: 0x000000
        }

        // Screen settings button.
        const screenButton = new Button(
            loader.getSpritesheet('Settings').textures['btn_choosetoplay_ad'],
            {
                content: 'Screen Setup and Audio Description',
                style: buttonTextStyle,
                offset: {x: 47, y: -2}
            }
        );
        this.disposables.add(new InteractiveWrapper(screenButton, {
            onHover: () => {
                popover.game.vo.playHighlight( 'CYB_PanelHome_Adjustscreen' );
            },
            onTap: () => {
                this.emit( "groupSelect", Menus.Type.Screen );
            }
        }));
        screenButton.position.set( 1080, 470 );
        this.addChild( screenButton );
        this.addButton( screenButton );

        // Sound settings button
        const soundButton = new Button(
            loader.getSpritesheet('Settings').textures['btn_choosetoplay_sounds'],
            {
                content: 'Sounds and Captioning',
                style: buttonTextStyle,
                offset: {x: 47, y: -2}
            }
        );
        this.disposables.add(new InteractiveWrapper(soundButton, {
            onHover: () => {
                popover.game.vo.playHighlight( 'CYB_PanelHome_Adjustsounds' );
            },
            onTap: () => {
                this.emit( "groupSelect", Menus.Type.Sounds );
            }
        }));
        soundButton.position.set( 1080, 618 );
        this.addChild( soundButton );
        this.addButton( soundButton );

        // Difficult settings button
        const difficultyButton = new Button(
            loader.getSpritesheet('Settings').textures['btn_choosetoplay_levels'],
            {
                content: 'Difficulty Levels, Hints and Controls',
                style: buttonTextStyle,
                offset: {x: 47, y: -2}
            }
        );
        this.disposables.add(new InteractiveWrapper(difficultyButton, {
            onHover: () => {
                popover.game.vo.playHighlight( 'CYB_PanelHome_Adjustdifficulty' );
            },
            onTap: () => {
                this.emit( "groupSelect", Menus.Type.Difficulty );
            }
        }));
        difficultyButton.position.set( 1080, 768 );
        this.addChild( difficultyButton );
        this.addButton( difficultyButton );
    }

    begin()
    {
        let focusButtons:Focusable[] = this.buttons.slice();
        for( let button of this.popover.buttons ) {
            focusButtons.push( button );
        }
        focusButtons.push(this.titleBg);

        FocusManager.instance.activateContext( focusButtons );

        if (this.popover.game.settings.playAudioDescriptions.value) {
            this.playAudioDescription()
                .then(() => this.playIntroAudio());
        }
        else
        {
            this.playIntroAudio();
        }
    }

    playAudioDescription(_overrideAudio:boolean=false):Promise<any>
    {
        return this.popover.game.vo.playVO( ["Cyberchase_Game_Set_Up_Game_1", "CYB_Panel_Tab_AD"], true );
    }

    async playIntroAudio()
    {
        this.popover.game.vo.playVO(['CYB_PanelHome_Autoplay', 'CYB_PanelHome_Playgame'], true);
    }

    playInfoAudio()
    {
        this.popover.game.vo.playVO( "CYB_PanelHome_InfoDescription", true );
    }

    destroy()
    {
        this.disposables.dispose();
        super.destroy({children: true});
    }

    private addButton( button:Button ) {
        this.buttons.push( button );
    }
}